/**
 * The calendar's markup, as pure functions from data to HTML strings.
 *
 * Kept apart from the page controller so it can be tested under Node with no browser: every
 * string that came from a person (a title, a place, a description, a name) goes through
 * escapeHtml here and nowhere else, and web/test/calendar.test.mjs holds that line by parsing
 * the output of hostile input in a real DOM. The page attaches behaviour with data attributes and
 * addEventListener -- there is no inline JavaScript and no inline style anywhere, because the
 * site's CSP forbids both.
 */
import { escapeHtml } from './utils.js';
import { dateKey, parseDateOnly, describeRepeat, repeatFromRrule, toFormTimes } from './calendar-model.js';

const MAX_CHIPS_PER_DAY = 3;
const WEEKDAY_HEADINGS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const timeLabel = (date) => date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const longDate = (date) => date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });

export function monthTitle(cursor) {
    return cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/** What to print beside an entry on one day: a start time, or "All day". */
export function entryTime(entry, day) {
    if (entry.allDay) return 'All day';
    return dateKey(entry.start) === dateKey(day) ? timeLabel(entry.start) : 'All day';
}

/** When an event happens, in words, for its first occurrence (or the one being looked at). */
export function describeWhen(event, occurrence = event) {
    if (event.all_day) {
        const first = parseDateOnly(occurrence.starts_at);
        const last = parseDateOnly(toFormTimes({ all_day: true, starts_at: occurrence.starts_at, ends_at: occurrence.ends_at }).end);
        if (dateKey(first) === dateKey(last)) return `${longDate(first)}, all day`;
        return `${longDate(first)} to ${longDate(last)}`;
    }
    const start = new Date(occurrence.starts_at);
    const end = new Date(occurrence.ends_at);
    if (start.toDateString() === end.toDateString()) return `${longDate(start)}, ${timeLabel(start)} to ${timeLabel(end)}`;
    return `${longDate(start)} ${timeLabel(start)} to ${longDate(end)} ${timeLabel(end)}`;
}

const chipClasses = (entry, viewerId) => [
    'cal-chip',
    entry.allDay ? 'cal-chip-allday' : '',
    entry.event.created_by === viewerId ? '' : 'cal-chip-theirs',
].filter(Boolean).join(' ');

/** One event on one day, as a button. Its date rides along so a recurring event opens on the occurrence clicked. */
function chip(entry, day, viewerId) {
    const time = entryTime(entry, day);
    return `
        <button type="button" class="${chipClasses(entry, viewerId)}" data-event="${Number(entry.event.id)}"
                data-start="${escapeHtml(entry.occurrence.starts_at)}" data-end="${escapeHtml(entry.occurrence.ends_at)}"
                title="${escapeHtml(`${entry.event.title}, ${time}`)}">
            ${entry.allDay ? '' : `<span class="cal-chip-time">${escapeHtml(time)}</span>`}
            <span class="cal-chip-title">${escapeHtml(entry.event.title)}</span>
        </button>`;
}

export function renderMonth(cursor, days, byDay, { today, viewerId }) {
    const heading = WEEKDAY_HEADINGS.map((d) => `<div class="cal-weekday" role="columnheader">${d}</div>`).join('');
    const cells = days.map((day) => {
        const key = dateKey(day);
        const entries = byDay.get(key) ?? [];
        const shown = entries.slice(0, MAX_CHIPS_PER_DAY);
        const hidden = entries.length - shown.length;
        const classes = [
            'cal-day',
            day.getMonth() === cursor.getMonth() ? '' : 'cal-day-other',
            key === dateKey(today) ? 'cal-day-today' : '',
        ].filter(Boolean).join(' ');
        return `
            <div class="${classes}" role="gridcell">
                <button type="button" class="cal-day-number" data-add="${key}"
                        aria-label="Add an event on ${escapeHtml(longDate(day))}">${day.getDate()}</button>
                <div class="cal-day-events">
                    ${shown.map((e) => chip(e, day, viewerId)).join('')}
                    ${hidden > 0 ? `<button type="button" class="cal-more" data-day="${key}">+${hidden} more</button>` : ''}
                </div>
            </div>`;
    }).join('');

    return `<div class="cal-grid" role="grid" aria-label="${escapeHtml(monthTitle(cursor))}">${heading}${cells}</div>`;
}

/** One row of the list view or a day's overflow dialog. */
function row(entry, day, viewerId) {
    const { event } = entry;
    const mine = event.created_by === viewerId;
    return `
        <div class="cal-row">
            <button type="button" class="cal-row-main" data-event="${Number(event.id)}"
                    data-start="${escapeHtml(entry.occurrence.starts_at)}" data-end="${escapeHtml(entry.occurrence.ends_at)}">
                <span class="cal-row-time">${escapeHtml(entryTime(entry, day))}</span>
                <span class="cal-row-body">
                    <span class="cal-row-title">${escapeHtml(event.title)}</span>
                    ${event.location ? `<span class="cal-row-meta"><i class="fa-solid fa-location-dot"></i> ${escapeHtml(event.location)}</span>` : ''}
                    ${mine ? '' : `<span class="cal-row-meta">Shared by ${escapeHtml(event.owner_name ?? 'a family member')}</span>`}
                </span>
            </button>
            <button type="button" class="btn btn-secondary btn-sm cal-row-add" data-download="${Number(event.id)}"
                    title="Add to your calendar" aria-label="Add ${escapeHtml(event.title)} to your calendar">
                <i class="fa-regular fa-calendar-plus"></i>
            </button>
        </div>`;
}

export function renderList(days, byDay, { viewerId }) {
    const sections = days
        .filter((day) => (byDay.get(dateKey(day)) ?? []).length > 0)
        .map((day) => `
            <section class="cal-list-day">
                <h2 class="cal-list-date">${escapeHtml(longDate(day))}</h2>
                ${byDay.get(dateKey(day)).map((entry) => row(entry, day, viewerId)).join('')}
            </section>`);
    return sections.length
        ? `<div class="cal-list">${sections.join('')}</div>`
        : `<div class="empty-state"><i class="fa-solid fa-calendar-days"></i><h2>Nothing this month</h2><p>Add an event to get the family calendar going.</p></div>`;
}

/** Everything on one day, for the "+n more" dialog. */
export function renderDay(day, entries, { viewerId }) {
    return `<div class="cal-list">${entries.map((entry) => row(entry, day, viewerId)).join('')}</div>`;
}

/** Who can see an event, in words. Only someone who can edit it knows the per-person list. */
export function describeVisibility(event, people, viewerId) {
    if (event.created_by !== viewerId && !event.can_edit) return `Shared by ${event.owner_name ?? 'a family member'}`;
    if (event.shared) return 'Everyone in the family';
    const names = (event.share_user_ids ?? []).map((id) => people.find((p) => p.id === id)?.full_name).filter(Boolean);
    return names.length ? names.join(', ') : 'Only you';
}

export function renderEventDetail(event, { occurrence, people, viewerId }) {
    const repeat = describeRepeat(event.rrule);
    return `
        <div class="cal-detail">
            <p class="cal-detail-when"><i class="fa-regular fa-clock"></i> ${escapeHtml(describeWhen(event, occurrence ?? event))}</p>
            ${repeat ? `<p class="cal-detail-line"><i class="fa-solid fa-rotate"></i> ${escapeHtml(repeat)}</p>` : ''}
            ${event.location ? `<p class="cal-detail-line"><i class="fa-solid fa-location-dot"></i> ${escapeHtml(event.location)}</p>` : ''}
            ${event.description ? `<p class="cal-detail-description">${escapeHtml(event.description)}</p>` : ''}
            <p class="cal-detail-line text-muted"><i class="fa-solid fa-user-group"></i> ${escapeHtml(describeVisibility(event, people, viewerId))}</p>
            <div class="modal-actions cal-detail-actions">
                <button type="button" class="btn btn-secondary" data-download="${Number(event.id)}">
                    <i class="fa-regular fa-calendar-plus"></i> Add to calendar
                </button>
                ${event.can_edit ? `
                    <button type="button" class="btn btn-secondary" data-edit="${Number(event.id)}"><i class="fa-solid fa-pen"></i> Edit</button>
                    <button type="button" class="btn btn-danger" data-delete="${Number(event.id)}"><i class="fa-solid fa-trash"></i> Delete</button>` : ''}
            </div>
        </div>`;
}

/**
 * The create and edit form. `event` is null for a new one, and `defaults` then carries the day
 * that was clicked and the starting times. Visibility defaults to everyone for a NEW event: a
 * family calendar where nothing shows to anyone until someone finds the picker would be a trap,
 * and it is one tick to narrow.
 */
export function renderEventForm({ event, defaults, people, limited }) {
    const editing = event !== null;
    const times = editing ? toFormTimes(event) : defaults;
    const allDay = editing ? event.all_day : defaults.allDay;
    const { repeat, until, custom } = editing ? repeatFromRrule(event.rrule, { allDay }) : { repeat: 'none', until: '', custom: false };
    const shared = editing ? event.shared : !limited;
    const chosen = new Set(event?.share_user_ids ?? []);
    const inputType = allDay ? 'date' : 'datetime-local';

    const people_ = people.length
        ? `<div class="share-people-list">
            ${people.map((u) => `
                <label class="share-person">
                    <input type="checkbox" class="share-person-check" value="${Number(u.id)}" ${chosen.has(u.id) ? 'checked' : ''}>
                    <span>${escapeHtml(u.full_name)}</span>
                </label>`).join('')}
           </div>`
        : '';

    const visibility = limited
        ? '<p class="form-hint">Events on this account are private to you.</p>'
        : `
            <fieldset class="cal-visibility">
                <legend>Who can see it</legend>
                <label class="share-person share-everyone">
                    <input type="checkbox" id="cal-shared" ${shared ? 'checked' : ''}>
                    <span>Everyone in the family</span>
                </label>
                ${people_}
                <p class="form-hint" id="cal-share-hint"></p>
            </fieldset>`;

    const option = (value, label) => `<option value="${value}" ${repeat === value && !custom ? 'selected' : ''}>${label}</option>`;

    return `
        <form id="cal-form" class="cal-form" novalidate>
            <div class="form-group">
                <label for="cal-title">Title</label>
                <input type="text" id="cal-title" maxlength="255" required value="${escapeHtml(event?.title ?? '')}" placeholder="Sunday lunch at Nan's">
            </div>
            <div class="setting-row">
                <label><input type="checkbox" id="cal-all-day" ${allDay ? 'checked' : ''}> All day</label>
            </div>
            <div class="cal-form-row">
                <div class="form-group">
                    <label for="cal-start">${allDay ? 'First day' : 'Starts'}</label>
                    <input type="${inputType}" id="cal-start" required value="${escapeHtml(times.start)}">
                </div>
                <div class="form-group">
                    <label for="cal-end">${allDay ? 'Last day' : 'Ends'}</label>
                    <input type="${inputType}" id="cal-end" required value="${escapeHtml(times.end)}">
                </div>
            </div>
            <div class="form-group">
                <label for="cal-location">Where <span class="text-faint">(optional)</span></label>
                <input type="text" id="cal-location" maxlength="512" value="${escapeHtml(event?.location ?? '')}">
            </div>
            <div class="form-group">
                <label for="cal-description">Notes <span class="text-faint">(optional)</span></label>
                <textarea id="cal-description" rows="3" maxlength="8000">${escapeHtml(event?.description ?? '')}</textarea>
            </div>
            <div class="cal-form-row">
                <div class="form-group">
                    <label for="cal-repeat">Repeats</label>
                    <select id="cal-repeat" class="form-select" ${custom ? 'disabled' : ''}>
                        ${custom ? '<option selected>Custom schedule (kept as it is)</option>' : ''}
                        ${option('none', 'Does not repeat')}
                        ${option('daily', 'Every day')}
                        ${option('weekly', 'Every week')}
                        ${option('monthly', 'Every month')}
                        ${option('yearly', 'Every year')}
                    </select>
                </div>
                <div class="form-group">
                    <label for="cal-until">Repeat until <span class="text-faint">(optional)</span></label>
                    <input type="date" id="cal-until" value="${escapeHtml(until)}" ${custom ? 'disabled' : ''}>
                </div>
            </div>
            ${visibility}
            ${event?.rrule ? '<p class="form-hint">This event repeats, so changes apply to every occurrence.</p>' : ''}
            <div id="cal-error" class="form-error hidden"></div>
            <div class="modal-actions">
                <button type="button" class="btn btn-secondary" id="cal-cancel">Cancel</button>
                <button type="submit" class="btn btn-primary">${editing ? 'Save changes' : 'Add event'}</button>
            </div>
        </form>`;
}

/**
 * The subscribe panel. The address is the whole credential (a calendar app cannot sign in), so
 * the copy says so plainly, and says that refresh is the calendar app's decision rather than ours.
 */
export function renderSubscribe(token) {
    const intro = `
        <p>Subscribe once and your family's events appear in Apple Calendar, Google Calendar or Outlook,
        and stay up to date. You'll see the same events you can see here.</p>`;

    if (!token?.token) {
        return `
            <div class="cal-subscribe-body">
                ${intro}
                <button type="button" class="btn btn-primary" id="cal-token-create"><i class="fa-solid fa-link"></i> Create my subscription link</button>
            </div>`;
    }

    return `
        <div class="cal-subscribe-body">
            ${intro}
            <label class="cal-url-label" for="cal-url">Your private calendar link</label>
            <div class="cal-url-field">
                <input type="text" id="cal-url" readonly value="${escapeHtml(token.url)}">
                <button type="button" class="btn btn-secondary" id="cal-url-copy"><i class="fa-regular fa-copy"></i> Copy</button>
                <a class="btn btn-primary" href="${escapeHtml(token.url)}"><i class="fa-regular fa-calendar-plus"></i> Subscribe</a>
            </div>
            <details class="cal-subscribe-more">
                <summary>Google Calendar, or a link that opens in the browser</summary>
                <p class="form-hint">Google Calendar does not use <code>webcal://</code> links. In Google Calendar choose
                "Other calendars", then "From URL", and paste this instead:</p>
                <input type="text" id="cal-url-https" readonly value="${escapeHtml(token.https_url)}">
            </details>
            <div class="cal-subscribe-note">
                <p><strong>How often it updates.</strong> Your calendar app decides that, not this site. Apple and Outlook
                usually look every hour or so; Google Calendar can take many hours, sometimes a day. A new or changed
                event can take that long to show up.</p>
                <p><strong>Keep the link to yourself.</strong> Anyone who has it can see every event you can see, and it
                needs no password. If it gets out, make a new one: the old link stops working straight away.</p>
            </div>
            <div class="cal-subscribe-actions">
                <button type="button" class="btn btn-secondary" id="cal-token-regenerate"><i class="fa-solid fa-arrows-rotate"></i> Make a new link</button>
                <button type="button" class="btn btn-danger" id="cal-token-revoke"><i class="fa-solid fa-ban"></i> Stop sharing this link</button>
            </div>
        </div>`;
}
